// fetchCanvasTriage — loads all tickets for /tickets/canvas board.
// Returns: { epics, groups, allTickets }
//   epics      — same shape as fetchEpicsTree() for the left-pane nav rail
//   groups     — pre-grouped by the requested groupBy key
//   allTickets — flat array for client-side re-grouping without refetch
//
// groupBy: 'story' | 'epic' | 'status' | 'priority' | 'assignee' | 'none'

import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

const WORKSPACE_ID = '69e5911201b0ef47b675463f'

const EPIC_ORDER = [
  'tickets', 'framework', 'canvas', 'server', 'integrations',
  'marketplace', 'chat', 'meet', 'symbols-system', 'documents',
  'pricing', 'commercials'
]
const HIDDEN_EPICS = new Set(['simona', 'user'])

const _isDone = (r) =>
  r.state === 'done' || r.state === 'CLOSED' || r.state === 'closed' || !!r.metadata?.struck

// Fetch all non-done open tickets + epic/story structure in one round-trip.
export const fetchCanvasTriage = async () => {
  const url = `${SUPABASE_URL}/rest/v1/tickets?select=id,external_id,type,title,state,priority,assignee_email,column_key,parent_ticket_id,metadata,labels,updated_at&workspace_id=eq.${WORKSPACE_ID}&deleted=eq.false&limit=3000`

  let rows = []
  try {
    const ctrl = new AbortController()
    const to = setTimeout(() => ctrl.abort(), 10000)
    const res = await fetch(url, { headers: restHeaders(), signal: ctrl.signal }).finally(() => clearTimeout(to))
    if (res.ok) rows = await res.json()
  } catch (_) {}

  if (!Array.isArray(rows) || !rows.length) return { epics: [], stories: [], allTickets: [] }

  // Index all rows by id.
  const byId = new Map()
  for (const r of rows) byId.set(r.id, r)

  // Build epic index.
  const epicByRowId = new Map()
  const epicByKey = new Map()
  for (const r of rows) {
    if (r.type !== 'epic') continue
    const key = r.metadata?.epicKey || r.external_id?.replace(/^EPIC#/, '')
    if (!key || HIDDEN_EPICS.has(key)) continue
    const epicInfo = {
      id: r.id,
      epicKey: key,
      title: r.title || key.toUpperCase(),
      state: r.state || 'not_started',
      donePct: 0,
      storiesTotal: 0, storiesDone: 0,
      tasksTotal: 0, tasksDone: 0,
      bugsTotal: 0, bugsDone: 0
    }
    epicByRowId.set(r.id, epicInfo)
    epicByKey.set(key, epicInfo)
  }

  // Synthesize missing product epics so the rail is always complete.
  for (const key of EPIC_ORDER) {
    if (!epicByKey.has(key)) {
      epicByKey.set(key, {
        id: null, epicKey: key, title: key.toUpperCase(),
        state: 'not_started', donePct: 0,
        storiesTotal: 0, storiesDone: 0,
        tasksTotal: 0, tasksDone: 0,
        bugsTotal: 0, bugsDone: 0
      })
    }
  }

  // Build story index (only stories parented to a non-hidden epic).
  const storyByRowId = new Map()
  const storyByKey = new Map()
  for (const r of rows) {
    if (r.type !== 'user_story') continue
    const storyKey = r.metadata?.storyKey || r.external_id?.replace(/^STORY#/, '') || `#${r.id}`
    const epicInfo = r.parent_ticket_id ? epicByRowId.get(r.parent_ticket_id) : null
    if (!epicInfo) continue
    const si = {
      id: r.id,
      storyKey,
      title: r.title || storyKey,
      state: r.state || 'not_started',
      epicKey: epicInfo.epicKey,
      epicTitle: epicInfo.title,
      donePct: 0,
      tasksTotal: 0, tasksDone: 0,
      bugsTotal: 0, bugsDone: 0,
      tickets: []
    }
    storyByRowId.set(r.id, si)
    storyByKey.set(r.id, si)
    // Roll up to epic
    epicInfo.storiesTotal += 1
    if (_isDone(r)) epicInfo.storiesDone += 1
  }

  // Build flat ticket array (tasks + bugs only, open). Attach parent refs.
  const allTickets = []
  for (const r of rows) {
    if (r.type !== 'task' && r.type !== 'bug') continue

    const parentStory = r.parent_ticket_id ? storyByRowId.get(r.parent_ticket_id) : null
    const parentEpicInfo = parentStory
      ? epicByKey.get(parentStory.epicKey)
      : (r.parent_ticket_id ? epicByRowId.get(r.parent_ticket_id) : null)

    const ticket = {
      id: r.id,
      external_id: r.external_id,
      type: r.type,
      title: r.title || '',
      state: r.state || 'not_started',
      priority: r.priority,
      assignee_email: r.assignee_email,
      column_key: r.column_key,
      labels: r.labels || [],
      metadata: r.metadata || {},
      updated_at: r.updated_at,
      parentStoryId: parentStory?.id || null,
      parentStoryTitle: parentStory?.title || '',
      parentStoryKey: parentStory?.storyKey || '',
      parentEpicKey: parentEpicInfo?.epicKey || '',
      parentEpicTitle: parentEpicInfo?.title || ''
    }

    allTickets.push(ticket)

    // Roll up counts.
    if (parentStory) {
      if (r.type === 'task') {
        parentStory.tasksTotal += 1
        if (_isDone(r)) parentStory.tasksDone += 1
      } else {
        parentStory.bugsTotal += 1
        if (_isDone(r)) parentStory.bugsDone += 1
      }
    }
    if (parentEpicInfo) {
      if (r.type === 'task') {
        parentEpicInfo.tasksTotal += 1
        if (_isDone(r)) parentEpicInfo.tasksDone += 1
      } else {
        parentEpicInfo.bugsTotal += 1
        if (_isDone(r)) parentEpicInfo.bugsDone += 1
      }
    }
  }

  // Compute donePct for stories and epics.
  for (const s of storyByRowId.values()) {
    const total = s.tasksTotal + s.bugsTotal
    s.donePct = total ? Math.round(((s.tasksDone + s.bugsDone) / total) * 100) : 0
  }
  for (const e of epicByKey.values()) {
    const total = e.tasksTotal + e.bugsTotal
    e.donePct = total ? Math.round(((e.tasksDone + e.bugsDone) / total) * 100) : 0
  }

  // Order epics by EPIC_ORDER.
  const orderedEpics = []
  const seenEpics = new Set()
  for (const key of EPIC_ORDER) {
    if (epicByKey.has(key)) { orderedEpics.push(epicByKey.get(key)); seenEpics.add(key) }
  }
  for (const [key, val] of epicByKey) {
    if (!seenEpics.has(key)) orderedEpics.push(val)
  }

  const orderedStories = Array.from(storyByRowId.values()).sort((a, b) => {
    const ai = EPIC_ORDER.indexOf(a.epicKey); const bi = EPIC_ORDER.indexOf(b.epicKey)
    const ar = ai === -1 ? 999 : ai; const br = bi === -1 ? 999 : bi
    if (ar !== br) return ar - br
    return a.storyKey.localeCompare(b.storyKey)
  })

  return { epics: orderedEpics, stories: orderedStories, allTickets }
}

// PATCH a single field on a ticket (used by drag-to-bucket).
// field: 'state' | 'priority' | 'assignee_email' | 'parent_ticket_id'
export const patchTicketField = async (ticketId, field, value) => {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/tickets?id=eq.${ticketId}`,
      {
        method: 'PATCH',
        headers: { ...restHeaders(), 'Content-Type': 'application/json', Prefer: 'return=minimal' },
        body: JSON.stringify({ [field]: value, updated_at: new Date().toISOString() })
      }
    )
    return res.ok
  } catch (_) {
    return false
  }
}

// Group a flat ticket array by a given key. Returns [{groupKey, groupLabel, tickets}].
export const groupTickets = (tickets, groupBy) => {
  if (!groupBy || groupBy === 'none') {
    return [{ groupKey: 'all', groupLabel: 'All Tickets', tickets }]
  }

  const groups = new Map()
  for (const t of tickets) {
    let key, label
    if (groupBy === 'story') {
      key = t.parentStoryId ? String(t.parentStoryId) : '_no_story'
      label = t.parentStoryTitle || 'No Story'
    } else if (groupBy === 'epic') {
      key = t.parentEpicKey || '_no_epic'
      label = t.parentEpicTitle || t.parentEpicKey?.toUpperCase() || 'No Epic'
    } else if (groupBy === 'status') {
      key = t.state || 'not_started'
      label = _stateLabel(t.state)
    } else if (groupBy === 'priority') {
      key = t.priority || '_none'
      label = t.priority || 'No Priority'
    } else if (groupBy === 'assignee') {
      key = t.assignee_email || '_unassigned'
      label = t.assignee_email || 'Unassigned'
    } else {
      key = 'all'; label = 'All Tickets'
    }
    if (!groups.has(key)) groups.set(key, { groupKey: key, groupLabel: label, tickets: [] })
    groups.get(key).tickets.push(t)
  }
  return Array.from(groups.values())
}

const _stateLabel = (s) => ({
  not_started: 'Not Started', in_progress: 'In Progress',
  blocked: 'Blocked', ready_to_test: 'Ready to Test',
  done: 'Done', closed: 'Closed', awaiting_approval: 'Awaiting Approval'
}[s] || (s || 'Not Started').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
